/**************************************************************************************************************************
 Copyright (c) 2024 WorkEarly Results LLP

     Name: intro.section.tsx
   Author: Harikrishnan Gangadharan
 Comments: 

/**************************************************************************************************************************
 IMPORTS
***************************************************************************************************************************/
import { type FunctionComponent, type ReactNode, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';

import Statics from '@/constants/statics';
import App from '@/constants/app';
import BasicComponents from '@/shared/basic.components';
import Assets from '@/constants/assets';
/**************************************************************************************************************************
 TYPES / GLOBAL DEFINITIONS
***************************************************************************************************************************/
interface AnimatedQuoteProps {
  title: ReactNode;
  subtitle: ReactNode;
  desc: string;
}

/**************************************************************************************************************************
 IMPLEMENTATIONS
***************************************************************************************************************************/
const AnimatedQuote: FunctionComponent<AnimatedQuoteProps> = ({
  title,
  subtitle,
  desc
}: AnimatedQuoteProps) => {
  return (
    <div className="w-full max-w-screen-md mx-auto flex flex-col items-center justify-center gap-4">
      <motion.h2
        className="text-3xl md:text-4xl lg:text-5xl text-white font-poppins font-extrabold flex flex-wrap items-center justify-center gap-4"
        initial={{ x: -200, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: -200, opacity: 0 }}
        transition={{
          duration: 0.5,
          delay: 0.5
        }}
      >
        {title}
      </motion.h2>
      <motion.h3
        className="text-3xl md:text-4xl lg:text-5xl text-white font-poppins font-extrabold flex flex-wrap items-center justify-center gap-4"
        initial={{ x: 200, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 200, opacity: 0 }}
        transition={{
          delay: 0.6,
          duration: 0.5
        }}
      >
        {subtitle}
      </motion.h3>
      <motion.p
        className="text-md lg:text-lg text-center text-white font-poppins font-medium tracking-[2px] mt-5 lg:mt-10"
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 50, opacity: 0 }}
        transition={{
          delay: 1.1,
          duration: 0.75
        }}
      >
        {desc}
      </motion.p>
    </div>
  );
};

const Home: FunctionComponent = () => {
  const { t } = useTranslation('home-page');

  const quotes = [
    <AnimatedQuote
      key={0}
      title={
        <Trans key="home-page.intro.quotes.one.first">
          Forging <span className="text-[45px] lg:text-[60px] text-themeYellow-500">Dreams</span>
        </Trans>
      }
      subtitle={
        <Trans key="home-page.intro.quotes.one.second">
          <span className="text-[45px] lg:text-[60px] text-themeBlue-300">Detailing</span> Reality
        </Trans>
      }
      desc={t('intro.quotes.one.desc')}
    />,
    <AnimatedQuote
      key={1}
      title={
        <Trans key="home-page.intro.quotes.two.first">
          Solution for all <span className="text-[45px] lg:text-[60px] text-themeYellow-500">Engineering</span>
        </Trans>
      }
      subtitle={
        <Trans key="home-page.intro.quotes.two.second">
          and <span className="text-[45px] lg:text-[60px] text-themePurple-300">Detailing</span> works
        </Trans>
      }
      desc={t('intro.quotes.two.desc')}
    />,
    <AnimatedQuote
      key={2}
      title={
        <Trans key="home-page.intro.quotes.three.first">
          From <span className="text-[45px] lg:text-[60px] text-themeYellow-500">Blueprint</span> to Reality
        </Trans>
      }
      subtitle={
        <Trans key="home-page.intro.quotes.three.second">
          with Accurate <span className="text-[45px] lg:text-[60px] text-themePurple-300">Steel</span> Detailing
        </Trans>
      }
      desc={t('intro.quotes.three.desc')}
    />
  ];

  const [currentBgIndex, setCurrentBgIndex] = useState<number>(0);
  const [currentDescIndex, setCurrentDescIndex] = useState<number>(0);

  const getNextIndex = () => {
    setCurrentBgIndex(currentBgIndex === Statics.IntroBgItems.length - 1 ? 0 : currentBgIndex + 1);
    setCurrentDescIndex(currentDescIndex === quotes.length - 1 ? 0 : currentDescIndex + 1);
  };

  useEffect(() => {
    const slideInterval = setInterval(getNextIndex, App.SlideInterval);

    return () => {
      clearInterval(slideInterval);
    };
  });

  return (
    <section id="home">
      <div className="relative w-full h-[100vh] overflow-hidden">
        <div className="bg-image w-full h-full">
          <img
            className="slowzoom w-full h-full object-cover"
            src={Statics.IntroBgItems[currentBgIndex].src}
            alt="intro"
          />
        </div>
        <div className="intro-text px-5">
          <AnimatePresence mode="wait">{quotes[currentDescIndex]}</AnimatePresence>
        </div>
      </div>
    </section>
  );
};

const Projects: FunctionComponent = () => {
  const { t } = useTranslation('projects');

  return (
    <BasicComponents.PageIntro
      asset={Assets.Images.INTRO_BG}
      title={t('intro.title')}
      desc={t('intro.desc')}
    />
  );
};

const Contact: FunctionComponent = () => {
  const { t } = useTranslation('contact-us');

  return (
    <BasicComponents.PageIntro
      asset={Assets.Images.INTRO_BG}
      title={t('intro.title')}
      desc={t('intro.desc')}
    />
  );
};

const Careers: FunctionComponent = () => {
  const { t } = useTranslation('careers');

  return (
    <BasicComponents.PageIntro
      asset={Assets.Images.INTRO_BG}
      title={t('intro.title')}
      desc={t('intro.desc')}
    />
  );
};

const Intros = {
  Home,
  Careers,
  Contact,
  Projects
};

export default Intros;
